/* eslint-disable react/jsx-props-no-spreading */
import { useRouter } from "next/navigation";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { Divider, InlineInfoBox } from "staysure-component-library";

import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import RichText from "@/components/atoms/rich-text/rich-text";
import { MedicalEdit } from "@/config/app-constant";
import useGtmCustomEvent from "@/hooks/gtm/use-gtm-custom-events";
import { useRenewalStateContext } from "@/providers/renewal-state-provider";
import { MedicalAccordionType } from "@/schema/renewal-page/middle-panel.schema";
import { RootState } from "@/store/root-reducer";

import BookTravellingBefore from "./book-travelling-before/book-travelling-before";
import MedicalAccordionSection from "./medical-accordion-section/medical-accordion-section";
import PlusIcon from "../travellers/plus-icon/plus-icon";

type MedicalContentProps = {
  medicalAccordionData: MedicalAccordionType;
};

function MedicalContent({
  medicalAccordionData,
}: Readonly<MedicalContentProps>) {
  const quote = useSelector((state: RootState) => state.quote);
  const router = useRouter();
  const { travellerDetails, mtaMedicalConsent } = quote.attributes;
  const { updateLastEditAccordion } = useRenewalStateContext();

  const isAnyCondition = useMemo(() => {
    return travellerDetails?.traveller?.some(
      (traveller) => traveller.medical?.condition
    );
  }, [travellerDetails]);

  const handleGtmEvent = useGtmCustomEvent({
    event: "custom_events",
    eventName: "add_edit_conditions",
    stepName: "renewal_medical_conditions",
    medicalConditionsAdded: "true",
  });

  return (
    <div className="flex flex-col gap-spacing-l md:gap-spacing-m lg:gap-spacing-l">
      <h2 className="text-2xs text-text-functional-1 font-[400] leading-[150%] font-body-font">
        {medicalAccordionData?.description}
      </h2>
      <InlineInfoBox
        content={
          <RichText richText={medicalAccordionData.infoBoxDescription} />
        }
        className="border-l-8 rounded-lg !border-border-info-primary bg-surface-info-2 text-text-body py-spacing-xs px-spacing-m leading-[150%] font-body-font"
      />

      <div className="mt-spacing-s md:mt-spacing-m lg:mt-spacing-xs">
        <MedicalAccordionSection {...medicalAccordionData} />

        <div>
          <div
            className={`${
              isAnyCondition ? "w-[216px]" : "w-[180px]"
            } mt-spacing-l md:mt-spacing-s`}
          >
            <LabelButtonWrapper
              label={
                isAnyCondition
                  ? medicalAccordionData.addEditCtaLabel
                  : medicalAccordionData?.addCtaLabel
              }
              hierarchy="secondary"
              isLeftIcon
              className="!h-[48px] leading-[18px] "
              leftIcon={<PlusIcon />}
              id="add-medical-conditions-button"
              onClick={() => {
                handleGtmEvent();
                router.push(`/medical-confirmation?${MedicalEdit}=true`);
                updateLastEditAccordion("medical-conditions");
              }}
            />
          </div>
        </div>

        {mtaMedicalConsent &&
          mtaMedicalConsent.isTravellingBeforeRenewal !== undefined && (
            <>
              <Divider margin={12} color="#DCDCDC" />
              <BookTravellingBefore
                {...medicalAccordionData}
                isTravellingBeforeRenewal={
                  mtaMedicalConsent.isTravellingBeforeRenewal
                }
              />
            </>
          )}
      </div>
    </div>
  );
}

export default MedicalContent;
